/**
 * Public catalog and checkout pricing for PahCertificar certificate requests.
 *
 * Electronic invoicing is paid directly through the PahCertificar checkout.
 * Virtual-token products are listed with their current customer-facing tariff
 * and selected inside the PahCertificar app before the app continues to the
 * secure purchase channel. Keeping both sets of prices here prevents the
 * marketing site and customer portal from drifting.
 *
 * Amounts are the final checkout price in COP — what the customer pays and
 * what Nequi collects (the portal presents it as IVA incluido; the Facturar
 * products carry the tax breakdown for billing). Prices cross the wire in
 * **centavos (COP × 100)** to match the payment providers' `value`
 * convention (Nequi, Wompi, etc.). The customer portal renders in COP.
 */
export const DEFAULT_VALIDITY_MONTHS = 12;
/** Known customer-facing catalog types, in display order. */
export const CERTIFICATE_TYPES = [
    "facturacion",
    "firma_natural",
    "firma_natural_rut",
    "firma_representante",
    "firma_juridica",
    "profesional",
    "funcion_publica",
    "funcion_publica_siif",
    "comunidad_academica",
];
/** Types covered by a current written instrument and enabled for checkout. */
export const TRANSACTIONAL_CERTIFICATE_TYPES = [
    "facturacion",
];
export const CERTIFICATE_PRICES_COP = {
    facturacion: { 12: 199_000 },
    firma_natural: {
        3: 89_972.81,
        6: 143_955.97,
        12: 209_937.42,
        24: 311_018.4,
    },
    firma_natural_rut: {
        3: 89_972.81,
        6: 143_955.97,
        12: 209_937.42,
        24: 311_018.4,
    },
    firma_representante: {
        3: 89_972.81,
        6: 143_955.97,
        12: 209_937.42,
        24: 311_018.4,
    },
    firma_juridica: {
        3: 89_972.81,
        6: 143_955.97,
        12: 209_937.42,
        24: 311_018.4,
    },
    profesional: {
        3: 89_972.81,
        6: 143_955.97,
        12: 209_937.42,
        24: 311_018.4,
    },
    funcion_publica: {
        3: 89_972.81,
        6: 143_955.97,
        12: 209_937.42,
        24: 311_018.4,
    },
    funcion_publica_siif: {
        3: 89_972.81,
        6: 143_955.97,
        12: 209_937.42,
        24: 311_018.4,
    },
    comunidad_academica: {
        3: 89_972.81,
        6: 143_955.97,
        12: 209_937.42,
        24: 311_018.4,
    },
};
/** Spanish display names — mirror the Pah Facturar product names. */
export const CERTIFICATE_TYPE_LABELS = {
    firma_natural: "Firma Digital Persona Natural",
    firma_natural_rut: "Firma Digital Persona Natural con RUT",
    firma_representante: "Firma Digital Representante Legal",
    firma_juridica: "Firma Digital Pertenencia a Empresa",
    profesional: "Firma Digital Profesional Titulado",
    funcion_publica: "Firma Digital Función Pública",
    funcion_publica_siif: "Firma Digital Función Pública SIIF Nación",
    comunidad_academica: "Firma Digital Comunidad Académica",
    facturacion: "Facturación Electrónica DIAN",
};
export const VALIDITY_LABELS = {
    3: "3 meses",
    6: "6 meses",
    12: "1 año",
    24: "2 años",
};
export function isCertificateType(value) {
    return typeof value === "string" && value in CERTIFICATE_PRICES_COP;
}
export function isCertificateTypeTransactional(type) {
    return TRANSACTIONAL_CERTIFICATE_TYPES.includes(type);
}
/** Validity periods purchasable for `type`, ascending. */
export function validityOptionsFor(type) {
    if (!isCertificateTypeTransactional(type))
        return [];
    return listedValidityOptionsFor(type);
}
/** Publicly listed periods for a product, whether paid here or continued later. */
export function listedValidityOptionsFor(type) {
    return Object.keys(CERTIFICATE_PRICES_COP[type])
        .map(Number)
        .sort((a, b) => a - b);
}
/** Public catalog price. This does not by itself enable PahCertificar checkout. */
export function listedPriceForCertificate(type, validityMonths = DEFAULT_VALIDITY_MONTHS) {
    const cop = CERTIFICATE_PRICES_COP[type][validityMonths];
    if (cop === undefined) {
        throw new Error(`No listed price for certificate type "${type}" with ${validityMonths}-month validity`);
    }
    return { cop, centavos: Math.round(cop * 100) };
}
export function isPricedCombination(type, validityMonths) {
    if (!isCertificateTypeTransactional(type))
        return false;
    return CERTIFICATE_PRICES_COP[type][validityMonths] !== undefined;
}
export function priceForCertificate(type, validityMonths = DEFAULT_VALIDITY_MONTHS) {
    if (!isCertificateTypeTransactional(type)) {
        throw new Error(`Certificate type "${type}" is not enabled for public checkout`);
    }
    return listedPriceForCertificate(type, validityMonths);
}
export function formatCop(cop, locale = "es-CO") {
    const fractionDigits = Number.isInteger(cop) ? 0 : 2;
    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format(cop);
}
//# sourceMappingURL=pricing.js.map